import { daysUntilExpiry, RENEWAL_PROOF_TYPES, Renewal } from '@iws/backoffice-api'
import { format } from 'date-fns'
import { FileUp } from 'lucide-react'
import { toast } from 'sonner'
import { useCallback, useEffect, useRef, useState } from 'react'

import { PageHeader } from '@/components/atoms'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Spinner } from '@/components/ui/spinner'
import { useAuth } from '@/contexts/AuthContext'
import { RenewalModel } from '@/http/models/Renewal'

const errorOf = (error: unknown, fallback: string) => (error as { data?: { message?: string } } | null)?.data?.message ?? fallback
const day = (d?: string | null) => (d ? format(new Date(d), 'd MMM yyyy') : '—')
const STATUS_TONE = { pending: 'warning', approved: 'success', rejected: 'error' } as const

/**
 * The business's subscription (POS.md §13.3): when it ends, and renewal by payment proof. System Admin reviews the
 * proof; approval moves the expiry one year on.
 */
export const SubscriptionPage = () => {
  const { can } = useAuth()
  const input = useRef<HTMLInputElement>(null)
  const [renewals, setRenewals] = useState<Renewal[]>([])
  const [expiration, setExpiration] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [file, setFile] = useState<File | null>(null)
  const [note, setNote] = useState('')
  const [sending, setSending] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const res = await RenewalModel.getList()
    if (res.success && res.data) {
      setRenewals(res.data.result)
      setExpiration(res.data.expiration_date)
    } else toast.error(errorOf(res.error, 'Could not load the subscription'))
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const pending = renewals.find((r) => r.status === 'pending')
  const days = daysUntilExpiry(expiration)

  const send = async () => {
    if (!file) return
    setSending(true)
    try {
      const target = await RenewalModel.getProofUploadUrl({ file_name: file.name, content_type: file.type })
      if (!target.success || !target.data) throw new Error(errorOf(target.error, 'Could not start the upload'))
      const put = await fetch(target.data.result.upload_url, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file })
      if (!put.ok) throw new Error('The upload failed. Try again.')
      const res = await RenewalModel.submit({ proof_path: target.data.result.path, proof_name: file.name, proof_type: file.type, note: note.trim() || undefined })
      if (!res.success) throw new Error(errorOf(res.error, 'Could not send the request'))
      toast.success('Renewal request sent', { description: 'You will see the result here once it is reviewed.' })
      setFile(null)
      setNote('')
      if (input.current) input.current.value = ''
      load()
    } catch (error) {
      toast.error((error as Error).message)
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Subscription" description="When your plan ends, and renewing it." />

      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>{expiration ? `Runs until ${day(expiration)}` : 'No end date set'}</CardTitle>
              <CardDescription>
                {days === null
                  ? 'Your plan has no expiry date.'
                  : days < 0
                    ? `Expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago. Renew to keep using IWS Commerce.`
                    : days === 0
                      ? 'Expires today.'
                      : `${days} day${days === 1 ? '' : 's'} left.`}
              </CardDescription>
            </CardHeader>
          </Card>

          {can('renewals:submit') && (
            <Card>
              <CardHeader>
                <CardTitle>Renew</CardTitle>
                <CardDescription>
                  Pay for the next year, then send the payment proof (bank slip, mobile banking receipt) as a PDF or photo.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {pending ? (
                  <p className="text-sm text-text-2">
                    Your request from {day(pending.created_at)} is waiting for review. You can send another once it is reviewed.
                  </p>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-3">
                      <Button variant="secondary" onClick={() => input.current?.click()}>
                        <FileUp />
                        {file ? 'Change file' : 'Choose proof'}
                      </Button>
                      <span className="text-sm text-text-2">{file?.name ?? 'No file chosen'}</span>
                      <input
                        ref={input}
                        type="file"
                        accept={RENEWAL_PROOF_TYPES.join(',')}
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0] ?? null
                          if (f && f.size > 10 * 1024 * 1024) return toast.error('Files up to 10 MB')
                          setFile(f)
                        }}
                      />
                    </div>
                    <div className="max-w-lg space-y-1.5">
                      <Label htmlFor="renewal-note">Note (optional)</Label>
                      <Input id="renewal-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Transaction ID, payer name" />
                    </div>
                    <Button onClick={send} disabled={!file || sending}>
                      {sending && <Spinner />}
                      Send for review
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Requests</CardTitle>
            </CardHeader>
            <CardContent>
              {renewals.length === 0 ? (
                <p className="py-4 text-center text-sm text-muted-foreground">No renewal requests yet.</p>
              ) : (
                <ul className="divide-y divide-border-subtle">
                  {renewals.map((r) => (
                    <li key={r._id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm">
                      <div className="min-w-0">
                        <p className="truncate text-ink">{r.proof_name}</p>
                        <p className="text-xs text-muted-foreground">
                          Sent {day(r.created_at)}
                          {r.submitted_by?.name ? ` by ${r.submitted_by.name}` : ''}
                          {r.status === 'approved' && r.new_expiration_date ? ` · now runs until ${day(r.new_expiration_date)}` : ''}
                          {r.status === 'rejected' && r.reject_reason ? ` · ${r.reject_reason}` : ''}
                        </p>
                      </div>
                      <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
